import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { companiesAPI, adminAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Building2, Plus, Pencil, Trash2, Loader2, MapPin, FileText, Users } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', address: '', tax_number: '', logo_url: '', assigned_users: [] });
  const [submitting, setSubmitting] = useState(false);
  const { isAdmin } = useAuth();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await companiesAPI.getAll();
      setCompanies(res.data);
      if (isAdmin) {
        try { const uRes = await adminAPI.getUsers(); setUsers(uRes.data.filter(u => u.role !== 'admin')); } catch {}
      }
    } catch { toast.error('Failed to load companies'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      if (editingCompany) { await companiesAPI.update(editingCompany.id, formData); toast.success('Company updated'); }
      else { await companiesAPI.create(formData); toast.success('Company added'); }
      setDialogOpen(false); resetForm(); fetchData();
    } catch { toast.error('Operation failed'); }
    finally { setSubmitting(false); }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData({ name: company.name, description: company.description || '', address: company.address || '', tax_number: company.tax_number || '', logo_url: company.logo_url || '', assigned_users: company.assigned_users || [] });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try { await companiesAPI.delete(id); toast.success('Company deleted'); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  const toggleUser = (userId) => {
    setFormData(prev => ({
      ...prev,
      assigned_users: prev.assigned_users.includes(userId) ? prev.assigned_users.filter(id => id !== userId) : [...prev.assigned_users, userId]
    }));
  };

  const resetForm = () => { setEditingCompany(null); setFormData({ name: '', description: '', address: '', tax_number: '', logo_url: '', assigned_users: [] }); };
  const openNewDialog = () => { resetForm(); setDialogOpen(true); };

  if (loading) {
    return (<Layout title="Companies"><div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></Layout>);
  }

  return (
    <Layout title="Companies">
      <div className="space-y-6" data-testid="companies-page">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading text-2xl font-bold">My Companies</h2>
            <p className="text-muted-foreground">Manage all your companies from here</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNewDialog} className="rounded-full" data-testid="add-company-btn"><Plus className="h-4 w-4 mr-2" />Add Company</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">{editingCompany ? 'Edit Company' : 'Add New Company'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Company Name *</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="ABC Technologies Inc." required data-testid="company-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Brief description about the company" data-testid="company-description-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input id="address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Istanbul, Turkey" data-testid="company-address-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tax_number">Tax Number</Label>
                  <Input id="tax_number" value={formData.tax_number} onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })} placeholder="1234567890" data-testid="company-tax-input" />
                </div>
                {isAdmin && users.length > 0 && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5"><Users className="h-4 w-4" />Assigned Users</Label>
                    <div className="grid grid-cols-2 gap-1.5 max-h-32 overflow-y-auto">
                      {users.map(u => (
                        <div key={u.id} className="flex items-center justify-between p-1.5 rounded bg-muted/30">
                          <span className="text-xs truncate">{u.full_name}</span>
                          <Switch checked={formData.assigned_users.includes(u.id)} onCheckedChange={() => toggleUser(u.id)} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={submitting} data-testid="company-submit-btn">
                    {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editingCompany ? 'Update' : 'Add'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {companies.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {companies.map((company) => (
              <Card key={company.id} className="card-hover" data-testid={`company-card-${company.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center"><Building2 className="h-6 w-6 text-primary" /></div>
                      <div>
                        <CardTitle className="font-heading text-lg">{company.name}</CardTitle>
                        <Badge variant={company.is_active ? 'default' : 'secondary'} className="mt-1">{company.is_active ? 'Active' : 'Inactive'}</Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {company.description && (<div className="flex items-start gap-2 text-sm text-muted-foreground"><FileText className="h-4 w-4 mt-0.5 shrink-0" /><span className="line-clamp-2">{company.description}</span></div>)}
                  {company.address && (<div className="flex items-center gap-2 text-sm text-muted-foreground"><MapPin className="h-4 w-4 shrink-0" /><span className="truncate">{company.address}</span></div>)}
                  {company.tax_number && (<div className="text-sm text-muted-foreground"><span className="font-medium">Tax ID:</span> {company.tax_number}</div>)}
                  {company.assigned_users?.length > 0 && (<div className="flex items-center gap-1.5 text-sm text-muted-foreground"><Users className="h-4 w-4 shrink-0" /><span>{company.assigned_users.length} user(s) assigned</span></div>)}
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => handleEdit(company)} data-testid={`edit-company-${company.id}`}><Pencil className="h-4 w-4 mr-1" />Edit</Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="outline" size="sm" className="text-destructive hover:text-destructive" data-testid={`delete-company-${company.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Company</AlertDialogTitle>
                          <AlertDialogDescription>Are you sure you want to delete "{company.name}"? This action cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(company.id)} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4"><Building2 className="h-8 w-8 text-primary" /></div>
              <h3 className="font-heading text-lg font-semibold mb-2">No companies yet</h3>
              <p className="text-muted-foreground mb-4">Get started by adding your first company</p>
              <Button onClick={openNewDialog} className="rounded-full"><Plus className="h-4 w-4 mr-2" />Add Company</Button>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}
